import { IonContent, IonIcon, IonPage, IonSpinner, useIonRouter } from "@ionic/react"
import { ImageVisualizer, InformativeModal, LoadingModal, ModalBackground, QuestionModal, SidebarMenu, SolidButton, TextArea, TextField } from "components";
import { useAppDispatch, useAppSelector, useForm } from "hooks";
import { useEffect, useRef, useState } from "react";
import { create, caretBack } from 'ionicons/icons';
import { convertTimestampToDate, createDocumentWithGeneratedId, getAddressErrorMessage, getCardCodeErrorMessage, getCardFNameErrorMessage, getCardNameErrorMessage, getContactPersonErrorMessage, getContactPhoneErrorMessage, getDocumentsByFilterArray, getDocumentById, getEmailErrorMessage, updateDocument, validateAddress, validateCardCode, validateCardFName, validateCardName, validateContactPerson, validateContactPhone, validateEmail, validateNitIvaCard, getNitIvaCardErrorMessage, validateNrcIvaCards, getNrcIvaCardsErrorMessage, uploadImageToFirebaseBucket } from "utils";
import { cloudUpload, ellipse, repeat } from 'ionicons/icons';
import { orderBy } from "firebase/firestore";
import { IvaCardToUpload } from "interfaces";
import { getDepartmensList, getDistrictsList, getGyresList, getMunicipalitiesList } from "services";
import { caretDown } from "ionicons/icons";
import { setIvaCardsState } from "store/iva-cards";

const formData = {
    address: '',
    cardCode: '',
    cardFName: '',
    cardName: '',
    email: '',
    nrc: '',
    numberDocument: '',
    contactPhone: '',
    contactPerson: '',
}

const formValidations = {
    address: [validateAddress, getAddressErrorMessage],
    cardCode: [validateCardCode, getCardCodeErrorMessage],
    cardFName: [validateCardFName, getCardFNameErrorMessage],
    cardName: [validateCardName, getCardNameErrorMessage],
    email: [validateEmail, getEmailErrorMessage],
    nrc: [validateNrcIvaCards, getNrcIvaCardsErrorMessage],
    numberDocument: [validateNitIvaCard, getNitIvaCardErrorMessage],
    contactPhone: [validateContactPhone, getContactPhoneErrorMessage],
    contactPerson: [validateContactPerson, getContactPersonErrorMessage],
}

interface LogInfo {
    $key: string,
    createdAt: string,
    sellerCode: string,
    sellerName: string
}

const authorizedRolesToEdit = [
    "admin",
    "adminComercial",
    "adminsupport"
]

export const IvaCardDetailPage = () => {
    const { token, role, uid } = useAppSelector(state => state.user);
    const { gyres, departments, municipalities, districts, businessCategories } = useAppSelector(state => state.ivaCards);

    const {
        cardCode, cardFName, cardName, numberDocument, nrc, address,
        email, contactPerson, contactPhone,

        cardCodeValid, cardFNameValid, cardNameValid, numberDocumentValid, nrcValid, addressValid,
        emailValid, contactPersonValid, contactPhoneValid,

        isFormValid, onInputChange, setFormState
    } = useForm(formData, formValidations);

    const [errorMessage, setErrorMessage] = useState<string>("");
    const [errorGettingUserInfo, setErrorGettingUserInfo] = useState<boolean>(false);
    const [loadingInformation, setLoadingInformation] = useState<boolean>(true);
    const [editEnabled, setEditEnabled] = useState<boolean>(false);

    const [showImageModal, setShowImageModal] = useState<boolean>(false);

    // estado donde se almacena la información de la tarjeta tal cual viene desde base
    const [clientInformation, setClientInformation] = useState<any>(null);

    // estado donde se almacenan los logs de la tarjeta de IVA
    const [vatLogs, setVatLogs] = useState<LogInfo[]>([]);

    // estado que sirve para almacenar una copia del estado del formulario editable
    const [copyFormState, setCopyFormState] = useState<any>(formData);

    // estado que sirve para almacenar el arreglo de imágenes que se muestran en 
    // el visor de imágenes
    const [imageToShow, setImageToShow] = useState<string[]>([]);

    // estados que nos sirven para mostrar los modales de confirmación del usuario
    const [showCancellationConfirmationModal, setShowCancellationConfirmationModal] = useState<boolean>(false);
    const [showEditConfirmationModal, setShowEditConfirmationModal] = useState<boolean>(false);

    // estado que nos sirve para mostrar un modal de carga mientras se está guardando la información editada
    const [savingInformation, setSavingInformation] = useState<boolean>(false);
    const [informationSavedSuccessfully, setInformationSavedSuccessfully] = useState<boolean>(false);
    const [errorSavingInformation, setErrorSavingInformation] = useState<boolean>(false);

    const [changingCardStatus, setChangingCardStatus] = useState<boolean>(false);

    const [gettingLists, setGettingLists] = useState<boolean>(false);

    const [frontalImage, setFrontalImage] = useState<string | null>(null);
    const [backImage, setBackImage] = useState<string | null>(null);

    const frontalImageInputRef = useRef(document.createElement("input"));
    const backImageInputRef = useRef(document.createElement("input"));

    const [frontalImageToUpload, setFrontalImageToUpload] = useState<IvaCardToUpload | null>(null);
    const [backImageToUpload, setBackImageToUpload] = useState<IvaCardToUpload | null>(null);

    // estado para almacenar la información de los departamentos, municipios, distritos
    // giros y categorías de negocios
    const [selectedDepartment, setSelectedDepartment] = useState<any>({});
    const [selectedMunicipality, setSelectedMunicipality] = useState<any>({});
    const [selectedDistrict, setSelectedDistrict] = useState<any>({});
    const [selectedGyre, setSelectedGyre] = useState<any>({});
    const [selectedCategory, setSelectedCategory] = useState<any>({});

    const ionRouter = useIonRouter();
    const dispatch = useAppDispatch();

    const [departmentsList, setDepartmentsList] = useState<any[]>(departments);
    const [municipalitiesList, setMunicipalitiesList] = useState<any[]>(municipalities);
    const [districtsList, setDistrictsList] = useState<any[]>(districts);
    const [gyresList, setGyresList] = useState<any[]>(gyres);
    const [categoriesList, setCategoriesList] = useState<any[]>(businessCategories);

    const [, , cardKey] = ionRouter.routeInfo.pathname.split("/");

    useEffect(() => {
        getClientInfo();
    }, []);

    const getClientInfo = async () => {
        try {
            setLoadingInformation(true);
            if (uid) {
                if (cardKey) {
                    const clientInfo = await getDocumentById("vat", cardKey);

                    if (clientInfo.status === 200 && clientInfo.data) {
                        let clientData = clientInfo.data;

                        // almacenamos la información de la tarjeta de IVA tal cual como viene
                        // desde la base de datos
                        setClientInformation(clientData);

                        setFrontalImage(clientData.imageFrontal);

                        setBackImage(clientData.imageBackground);

                        let newFormData = {
                            address: clientData.address?.toUpperCase(),
                            cardCode: clientData.cardCode,
                            cardFName: clientData.cardFName?.toUpperCase(),
                            cardName: clientData.cardName?.toUpperCase(),
                            email: clientData.email,
                            nrc: clientData.nrc,
                            numberDocument: clientData.numberDocument,
                            contactPhone: clientData.phoneNumber,
                            contactPerson: clientData.providedBy?.toUpperCase(),
                        }

                        setFormState(newFormData);
                        setCopyFormState(newFormData);

                        // DEPARTAMENTO
                        let newSelectedDepartment = {
                            codeDepartament: clientData.codeDepartament ?? '',
                            departament: clientData.departament?.toUpperCase() ?? '',
                            showOptions: false,
                        }

                        setSelectedDepartment(newSelectedDepartment);

                        // MUNICIPIO
                        let newSelectedMunicipality = {
                            codeMunicipality: clientData.codeMunicipality ?? '',
                            municipality: clientData.municipality?.toUpperCase() ?? '',
                            showOptions: false,
                        }

                        setSelectedMunicipality(newSelectedMunicipality);

                        // DISTRITO
                        let newSelectedDistrict = {
                            codeDistrict: clientData.codeDistrict ?? '',
                            district: clientData.district?.toUpperCase() ?? '',
                            showOptions: false,
                        }

                        setSelectedDistrict(newSelectedDistrict);

                        // GIROS
                        let newSelectedGyre = {
                            codeActivity: clientData.codeActivity ?? '',
                            economicActivity: clientData.economicActivity?.toUpperCase() ?? '',
                            showOptions: false,
                        }

                        setSelectedGyre(newSelectedGyre);

                        // TIPO DE EMPRESA
                        let newSelectedCategory = {
                            codeCategory: clientData.codeCategory ?? '',
                            nameCategory: clientData.nameCategory?.toUpperCase() ?? '',
                            showOptions: false,
                        }

                        setSelectedCategory(newSelectedCategory);

                        // Ejecutamos la función que recupera los logs
                        let filters = [
                            orderBy("createdAt", "desc")
                        ]

                        const logsResp = await getDocumentsByFilterArray(`vat/${cardKey}/logs`, filters);

                        let logsArray: LogInfo[] = [];

                        if (logsResp.status === 200 && logsResp.data) {
                            logsArray = logsResp.data.map((item: any) => {
                                return {
                                    $key: item.$key,
                                    createdAt: convertTimestampToDate(item.createdAt),
                                    sellerCode: item.sellerCode,
                                    sellerName: item.sellerName
                                }
                            })
                        } else {
                            const sellerInfo = await getDocumentById("accounts", clientInfo.data.createdBy);

                            if (sellerInfo.status === 200 && sellerInfo.data) {
                                logsArray.push({
                                    $key: clientInfo.data.createdAt,
                                    createdAt: convertTimestampToDate(clientInfo.data.createdAt),
                                    sellerCode: sellerInfo.data.salesPersonCode,
                                    sellerName: `${sellerInfo.data.name} ${sellerInfo.data.last_name}`
                                })
                            }
                        }

                        setVatLogs(logsArray);
                        setErrorGettingUserInfo(false);
                    } else {
                        throw new Error("Ocurrió un error al intentar recuperar la información del cliente. Intenta recargar nuevamente la página y si el error persiste comunícate con el equipo de soporte técnico");
                    }
                } else {

                }
            }
        } catch (error: any) {
            setClientInformation(null);
            setErrorMessage(error.message);
            setErrorGettingUserInfo(true);
        } finally {
            setLoadingInformation(false);
        }
    }

    const clickImageHandler = (image: string | null) => {
        if (image) {
            setShowImageModal(true);

            const imageArray = [image];

            setImageToShow(imageArray);
        }
    }

    // Función para guardar en local las imágenes que desea subir el usuario
    const changeIvaCardImage = (event: React.ChangeEvent<HTMLInputElement>, type: "frontal" | "back") => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];

            let [, extension] = file.name.split(".");

            let newIvaCardFile: IvaCardToUpload = {
                file,
                downloadUrl: URL.createObjectURL(file),
                extension
            }

            if (type === "frontal") {
                setFrontalImageToUpload(newIvaCardFile)
            } else {
                setBackImageToUpload(newIvaCardFile)
            }
        }
    }

    // Función para guardar la nueva información de la tarjeta de IVA que ha 
    // editado el usuario
    const saveNewInfo = async () => {
        setShowEditConfirmationModal(false);
        setSavingInformation(true);

        // verificamos si la información ya se ha modificado anteriormente consultando si existen logs o no
        const logs = await getDocumentsByFilterArray(`vat/${cardKey}/logs`);

        // si la información NO ha sido modificada anteriormente creamos un log con la información
        // de cómo fue creado el documento
        if (logs.status !== 200) {
            let ivaCardActualInfo = { ...clientInformation, action: "create" };

            const sellerInfo = await getDocumentById("accounts", clientInformation.createdBy);

            if (sellerInfo.status === 200 && sellerInfo.data) {
                ivaCardActualInfo = { ...ivaCardActualInfo, sellerCode: sellerInfo.data.salesPersonCode, sellerName: `${sellerInfo.data.name} ${sellerInfo.data.last_name}` }
            }

            await createDocumentWithGeneratedId(`vat/${cardKey}/logs`, ivaCardActualInfo);
        }

        let imageBackground = clientInformation.imageBackground;
        let imageFrontal = clientInformation.imageFrontal;

        // verificamos si el usuario quiere modificar alguna de las fotografías de la tarjeta
        if (frontalImageToUpload) {
            const uploadImageResp = await uploadImageToFirebaseBucket(frontalImageToUpload.file, `vats/${clientInformation.cardCode}`, `${clientInformation.cardCode}-FrontalImage.${frontalImageToUpload.extension}`)

            console.log(uploadImageResp);

            if (uploadImageResp.ok) {
                imageFrontal = uploadImageResp.downloadURL
            }

            setFrontalImageToUpload(null);
        }

        if (backImageToUpload) {
            const uploadImageResp = await uploadImageToFirebaseBucket(backImageToUpload.file, `vats/${clientInformation.cardCode}`, `${clientInformation.cardCode}-BackImage.${backImageToUpload.extension}`)

            console.log(uploadImageResp);

            if (uploadImageResp.ok) {
                imageBackground = uploadImageResp.downloadURL
            }

            setBackImageToUpload(null);
        }

        // guardamos en la base de datos la información modificada
        // copiamos la información de la tarjeta de IVA tal cual se encuentra en la base de datos
        let newInfo: any = {
            ...clientInformation,
            address,
            cardCode,
            cardFName,
            cardName,
            codeActivity: selectedGyre.codeActivity,
            codeCategory: selectedCategory.codeCategory,
            codeDepartament: selectedDepartment.codeDepartament,
            codeDistrict: selectedDistrict.codeDistrict,
            codeMunicipality: selectedMunicipality.codeMunicipality,
            departament: selectedDepartment.departament,
            district: selectedDistrict.district,
            economicActivity: selectedGyre.economicActivity,
            email,
            imageBackground,
            imageFrontal,
            municipality: selectedMunicipality.municipality,
            nameCategory: selectedCategory.nameCategory,
            nrc,
            numberDocument,
            phoneNumber: contactPhone,
            providedBy: contactPerson,
        };

        const updateResp = await updateDocument("vat", cardKey, newInfo);

        if (updateResp.status === 200) {
            // guardamos el log con las modificaciones realizadas
            if (uid) {
                const sellerInfo = await getDocumentById("accounts", uid);

                newInfo = { ...newInfo, action: "update", sellerCode: sellerInfo.data.salesPersonCode, sellerName: `${sellerInfo.data.name} ${sellerInfo.data.last_name}` }
            }

            newInfo = { ...newInfo, createdAt: new Date() }

            await createDocumentWithGeneratedId(`vat/${cardKey}/logs`, newInfo);

            setInformationSavedSuccessfully(true);
        } else {
            setErrorSavingInformation(true);
        }

        setSavingInformation(false);
    }

    // Función que se ejecuta cuando el usuario decide cancelar la edición
    // de los datos
    const cancelEditionInfoHandler = () => {
        // regresamos el formulario a su estado original
        setFormState(copyFormState);

        setEditEnabled(false);
    }

    // Función que se ejecuta cuando el usuario quiere salir de la página
    const backClickHandler = () => {
        if (editEnabled) {
            setShowCancellationConfirmationModal(true);
        } else {
            ionRouter.push("/ivaCards", "back", "push");
        }
    }

    // Función que se ejecuta cuando el usuario da click sobre el botón "Aceptar"
    // del mensaje de confirmación que aparece cuando los datos han sido actualizados
    const acceptSuccessfulMessageHandler = () => {
        setEditEnabled(false);
        setInformationSavedSuccessfully(false);

        getClientInfo();
    }

    // Función que se ejecuta para cambiar el estado de la tarjeta de IVA (pendiente o revisada)
    const changeIvaCardStatus = async () => {
        if (role && authorizedRolesToEdit.includes(role)) {
            setChangingCardStatus(true);

            let newStatus = true;

            if (clientInformation.hasOwnProperty("status")) {
                newStatus = !clientInformation.status;
            }

            let newClientInfo = {
                ...clientInformation,
                status: newStatus
            }

            await updateDocument("vat", cardKey, newClientInfo);

            setClientInformation(newClientInfo);

            setChangingCardStatus(false);
        }
    }

    // Función que se ejecuta cuando el usuario quiere habilitar la edición de los datos
    const enableEditingInformation = async () => {
        if (token) {
            setGettingLists(true);

            // Validamos si la información de los departamentos, municipios, distritos y giros ya se encuentra almacenada
            if (gyres.length === 0 || departments.length === 0 || municipalities.length === 0 || districts.length === 0) {
                // recuperamos la información de las listas
                const gyresResp = await getGyresList(token);

                setGyresList(gyresResp);

                const departmentsResp = await getDepartmensList(token);

                setDepartmentsList(departmentsResp);

                const municipalitiesResp = await getMunicipalitiesList(token);

                setMunicipalitiesList(municipalitiesResp);

                const districtsResp = await getDistrictsList(token);

                setDistrictsList(districtsResp);

                let ivaCardsPayload = {
                    gyres: gyresResp,
                    departments: departmentsResp,
                    municipalities: municipalitiesResp,
                    districts: districtsResp,
                }

                dispatch(setIvaCardsState(ivaCardsPayload));
            }

            setEditEnabled(true);

            setGettingLists(false);
        }

    }

    const showGyres = () => {
        let newSelectedGyreState = {
            ...selectedGyre,
            showOptions: !selectedGyre.showOptions,
        }

        setSelectedGyre(newSelectedGyreState);
    }

    const changeGyre = (code: string, description: string) => {
        let newSelectedGyreState = {
            codeActivity: code,
            economicActivity: description.toUpperCase(),
            showOptions: false,
        }

        setSelectedGyre(newSelectedGyreState);

        setGyresList(gyres);
    }

    const showDepartments = () => {
        let newSelectedDepartmentState = {
            ...selectedDepartment,
            showOptions: !selectedDepartment.showOptions,
        }

        setSelectedDepartment(newSelectedDepartmentState);
    }

    const changeDepartment = (code: string, description: string) => {
        let newSelectedDepartmentState = {
            codeDepartament: code,
            departament: description.toUpperCase(),
            showOptions: false,
        }

        setSelectedDepartment(newSelectedDepartmentState);

        setDepartmentsList(departments);
    }

    const showMunicipalities = () => {
        let newSelectedMunicipalityState = {
            ...selectedMunicipality,
            showOptions: !selectedMunicipality.showOptions,
        }

        setSelectedMunicipality(newSelectedMunicipalityState);
    }

    const changeMunicipality = (code: string, description: string) => {
        let newSelectedMunicipalityState = {
            codeMunicipality: code,
            municipality: description.toUpperCase(),
            showOptions: false,
        }

        setSelectedMunicipality(newSelectedMunicipalityState);

        setMunicipalitiesList(municipalities);
    }

    const showDistricts = () => {
        let newSelectedDistrictState = {
            ...selectedDistrict,
            showOptions: !selectedDistrict.showOptions,
        }

        setSelectedDistrict(newSelectedDistrictState);
    }

    const changeDistrict = (code: string, description: string) => {
        let newSelectedDistrictState = {
            codeDistrict: code,
            district: description.toUpperCase(),
            showOptions: false,
        }

        setSelectedDistrict(newSelectedDistrictState);

        setDistrictsList(districts);
    }

    const showCategories = () => {
        let newSelectedCategoryState = {
            ...selectedCategory,
            showOptions: !selectedCategory.showOptions,
        }

        setSelectedCategory(newSelectedCategoryState);
    }

    const changeCategory = (code: string, description: string) => {
        let newSelectedCategoryState = {
            codeCategory: code,
            nameCategory: description.toUpperCase(),
            showOptions: false,
        }

        setSelectedCategory(newSelectedCategoryState);

        setCategoriesList(businessCategories);
    }

    const searchGyre = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            let newGyreList = gyres.filter((item: any) => item.Descr.toUpperCase().includes(value.toUpperCase()));

            setGyresList(newGyreList);
        } else {
            setGyresList(gyres);
        }
    }

    const searchDepartment = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            let newList = departments.filter((item: any) => item.Descr.toUpperCase().includes(value.toUpperCase()));

            setDepartmentsList(newList);
        } else {
            setDepartmentsList(departments);
        }
    }

    const searchMunicipality = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            let newList = municipalities.filter((item: any) => item.Descr.toUpperCase().includes(value.toUpperCase()));

            setMunicipalitiesList(newList);
        } else {
            setMunicipalitiesList(municipalities);
        }
    }

    const searchDistricts = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            let newList = districts.filter((item: any) => item.Descr.toUpperCase().includes(value.toUpperCase()));

            setDistrictsList(newList);
        } else {
            setDistrictsList(districts);
        }
    }

    const searchCategory = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            let newList = businessCategories.filter((item: any) => item.name.toUpperCase().includes(value.toUpperCase()));

            setCategoriesList(newList);
        } else {
            setCategoriesList(businessCategories);
        }
    }

    return (
        <IonPage>
            <IonContent>
                <div className="w-full h-screen grid grid-cols-12">
                    <div className="col-span-2 border-r-2 border-solid border-light">
                        <SidebarMenu pageName="iva_cards" />
                    </div>

                    <div className="w-full h-screen flex flex-col col-span-10">
                        <div className={`w-full p-4 border-b-2 border-solid border-tertiary ${!loadingInformation && errorGettingUserInfo && "flex items-center gap-2"}`}>
                            {
                                !loadingInformation && errorGettingUserInfo &&
                                <IonIcon onClick={() => ionRouter.push("/ivaCards", "back", "push")} icon={caretBack} className="text-primary cursor-pointer text-[20px]" />
                            }

                            <p className="font-bold">Tarjetas de I.V.A.</p>
                        </div>

                        {
                            !loadingInformation && !errorGettingUserInfo && clientInformation &&
                            <div className="w-full flex flex-col gap-4 p-4 border-b-2 border-solid border-tertiary">
                                <div className="w-full flex justify-between gap-2">
                                    <div className="flex gap-4">
                                        <div className="flex items-center gap-2">
                                            <IonIcon onClick={backClickHandler} icon={caretBack} className="text-primary cursor-pointer text-[20px]" />
                                            <p className="font-semibold">{clientInformation.cardCode}</p>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            {
                                                clientInformation.hasOwnProperty("status") && clientInformation.status === true &&
                                                <div onClick={changeIvaCardStatus} className={`flex items-center gap-1 px-2 py-1 border border-tertiary rounded-full ${(role && authorizedRolesToEdit.includes(role) && 'cursor-pointer')}`}>
                                                    <IonIcon icon={ellipse} className="text-success text-[22px]" />
                                                    <p className="text-success text-large font-bold">Revisado</p>
                                                    {
                                                        role && authorizedRolesToEdit.includes(role) &&
                                                        <IonIcon icon={repeat} className="text-[22px]" />
                                                    }
                                                </div>
                                            }

                                            {
                                                (!clientInformation.hasOwnProperty("status") || clientInformation.status === false) &&
                                                <div onClick={changeIvaCardStatus} className="flex items-center gap-1 cursor-pointer px-2 py-1 border border-tertiary rounded-full">
                                                    <IonIcon icon={ellipse} className="text-progress-bar-orange text-[22px]" />
                                                    <p className="text-progress-bar-orange text-large font-bold">Pendiente</p>
                                                    {
                                                        role && authorizedRolesToEdit.includes(role) &&
                                                        <IonIcon icon={repeat} className="text-[22px]" />
                                                    }
                                                </div>
                                            }

                                            {/* <button
                                                onClick={() => setEditEnabled(!editEnabled)}
                                                className="bg-tertiary hover:opacity-80 rounded-md font-bold px-4 py-2 text-small flex items-center gap-1"
                                            >
                                                <IonIcon icon={repeat} className="text-[18px] mb-[2px]" />
                                                Cambiar estado
                                            </button> */}
                                        </div>

                                    </div>


                                    <div className="flex items-center gap-4">
                                        {
                                            editEnabled &&
                                            <>
                                                <button
                                                    onClick={cancelEditionInfoHandler}
                                                    className="bg-secondary text-white font-bold px-4 py-1 rounded-md"
                                                >
                                                    CANCELAR
                                                </button>

                                                <button
                                                    onClick={() => setShowEditConfirmationModal(true)}
                                                    disabled={!isFormValid}
                                                    className={`bg-success text-white font-bold px-4 py-1 rounded-md ${!isFormValid && 'opacity-40'}`}
                                                >
                                                    GUARDAR
                                                </button>
                                            </>
                                        }

                                        {
                                            !editEnabled && (role === "admin" || role === "adminComercial" || role === "adminsupport") &&
                                            <button
                                                onClick={enableEditingInformation}
                                                className="bg-tertiary hover:opacity-80 rounded-md font-bold px-4 py-2 text-small flex items-center gap-1"
                                            >
                                                <IonIcon icon={create} className="text-[18px] mb-[2px]" />
                                                EDITAR INFORMACIÓN
                                            </button>
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        <div id="main-section" className="w-full h-screen overflow-y-scroll grid grid-cols-10">
                            {
                                loadingInformation &&
                                <div className="w-full flex flex-col justify-center items-center col-span-10">
                                    <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
                                </div>
                            }

                            {
                                !loadingInformation && errorGettingUserInfo &&
                                <div className="w-full flex flex-col justify-center items-center p-4 col-span-10">
                                    <div className="w-3/5 flex flex-col items-center gap-4">
                                        <p className="font-semibold text-primary text-center">{errorMessage}</p>

                                        <div className="w-3/5">
                                            <SolidButton
                                                text="Recargar"
                                                backgroundColor="primary"
                                                disabled={false}
                                                onClickHandler={getClientInfo}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }

                            {
                                !loadingInformation && !errorGettingUserInfo && clientInformation &&
                                <div className="w-full flex flex-col items-center col-span-10">
                                    <div className="w-full p-8 grid grid-cols-2 gap-4">
                                        <div className="w-full flex flex-col pb-16 text-mid order-1 gap-6">
                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="cardCode"
                                                            label="CÓDIGO DE CLIENTE"
                                                            value={cardCode}
                                                            inputMode="text"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={cardCodeValid}
                                                            errorMessage={cardCodeValid}
                                                            required={true}
                                                            disabled={true}
                                                        />
                                                    </div>

                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">CÓDIGO DE CLIENTE</p>
                                                        <p className="font-bold">{clientInformation.cardCode?.toUpperCase()}</p>
                                                    </>
                                                }
                                            </div>

                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="cardFName"
                                                            label="NOMBRE COMERCIAL"
                                                            value={cardFName}
                                                            inputMode="text"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={cardFNameValid}
                                                            errorMessage={cardFNameValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">NOMBRE COMERCIAL</p>
                                                        <p className="font-bold">{clientInformation.cardFName?.toUpperCase()}</p>
                                                    </>
                                                }
                                            </div>

                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="cardName"
                                                            label="NOMBRE DEL CONTRIBUYENTE"
                                                            value={cardName}
                                                            inputMode="text"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={cardNameValid}
                                                            errorMessage={cardNameValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">NOMBRE DEL CONTRIBUYENTE</p>
                                                        <p className="font-bold">{clientInformation.cardName?.toUpperCase()}</p>
                                                    </>
                                                }
                                            </div>
                                        </div>

                                        <div className="w-full grid pb-32 text-mid order-2 gap-6">
                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="numberDocument"
                                                            label="NÚMERO DE IDENTIFICACIÓN TRIBUTARIA (N.I.T.)"
                                                            value={numberDocument}
                                                            inputMode="numeric"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={numberDocumentValid}
                                                            errorMessage={numberDocumentValid}
                                                            required={true}
                                                            maxLength={17}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">NÚMERO DE IDENTIFICACIÓN TRIBUTARIA (N.I.T.)</p>
                                                        <p className="font-bold">{clientInformation.numberDocument}</p>
                                                    </>
                                                }
                                            </div>

                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="nrc"
                                                            label="NÚMERO DE REGISTRO (N.R.C.)"
                                                            value={nrc}
                                                            inputMode="numeric"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={nrcValid}
                                                            errorMessage={nrcValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">NÚMERO DE REGISTRO (N.R.C.)</p>
                                                        <p className="font-bold">{clientInformation.nrc}</p>
                                                    </>
                                                }
                                            </div>
                                        </div>

                                        <div className="w-full grid pb-16 text-mid order-3 gap-6">
                                            <div className="flex flex-col gap-1">
                                                <p className="font-semibold text-medium">GIRO O ACTIVIDAD ECONÓMICA</p>
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2 flex flex-col">
                                                        <div
                                                            className={`flex justify-between items-center border pl-2 rounded-md ${selectedGyre && selectedGyre.hasOwnProperty("economicActivity") && selectedGyre.economicActivity.length > 0 ? 'border-success' : 'border-danger'}`}
                                                        >
                                                            <p className="py-2">{selectedGyre?.economicActivity.length > 0 ? selectedGyre?.economicActivity : 'Seleccione un giro'}</p>

                                                            <div
                                                                onClick={showGyres}
                                                                className={`h-full flex items-center border-l px-4 cursor-pointer ${selectedGyre && selectedGyre.hasOwnProperty("economicActivity") && selectedGyre.economicActivity.length > 0 ? 'border-success' : 'border-danger'}`}>
                                                                <IonIcon
                                                                    icon={caretDown}
                                                                    className="text-[20px]"
                                                                />
                                                            </div>

                                                        </div>

                                                        {
                                                            selectedGyre?.showOptions &&
                                                            <div className="flex flex-col border-l border-b border-r border-tertiary rounded-b-md max-h-[250px]">
                                                                <div className="p-4 border-b border-tertiary">
                                                                    <input
                                                                        type="text"
                                                                        id="gyres-filter"
                                                                        name="gyres-filter"
                                                                        placeholder="Escribe aquí para buscar"
                                                                        className="w-full border border-solid border-primary p-2 rounded-md"
                                                                        onChange={searchGyre}
                                                                    />
                                                                </div>

                                                                <div className="flex flex-col overflow-y-auto">
                                                                    {
                                                                        gyresList.map((gyreItem: any) => (
                                                                            <div
                                                                                key={gyreItem.FldValue}
                                                                                onClick={() => changeGyre(gyreItem.FldValue, gyreItem.Descr)}
                                                                                className="flex items-center gap-2 px-4 py-2 border-b border-tertiary last:border-b-0 cursor-pointer hover:bg-light"
                                                                            >
                                                                                <p>{gyreItem.Descr}</p>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>

                                                            </div>
                                                        }
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <p className="font-bold">{clientInformation.economicActivity?.toUpperCase()}</p>
                                                }
                                            </div>

                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextArea
                                                            name="address"
                                                            label="DIRECCIÓN"
                                                            value={address}
                                                            inputMode="text"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={addressValid}
                                                            errorMessage={addressValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">DIRECCIÓN</p>
                                                        <p className="font-bold">{clientInformation.address?.toUpperCase()}</p>
                                                    </>
                                                }
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <p className="font-semibold text-medium">DEPARTAMENTO</p>
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2 flex flex-col">
                                                        <div
                                                            className={`flex justify-between items-center border pl-2 rounded-md ${selectedDepartment && selectedDepartment.hasOwnProperty("departament") && selectedDepartment.departament.length > 0 ? 'border-success' : 'border-danger'}`}
                                                        >
                                                            <p className="py-2">{selectedDepartment?.departament.length > 0 ? selectedDepartment?.departament : 'Seleccione un departamento'}</p>

                                                            <div
                                                                onClick={showDepartments}
                                                                className={`h-full flex items-center border-l px-4 cursor-pointer ${selectedDepartment && selectedDepartment.hasOwnProperty("departament") && selectedDepartment.departament.length > 0 ? 'border-success' : 'border-danger'}`}>
                                                                <IonIcon
                                                                    icon={caretDown}
                                                                    className="text-[20px]"
                                                                />
                                                            </div>

                                                        </div>

                                                        {
                                                            selectedDepartment?.showOptions &&
                                                            <div className="flex flex-col border-l border-b border-r border-tertiary rounded-b-md max-h-[250px]">
                                                                <div className="p-4 border-b border-tertiary">
                                                                    <input
                                                                        type="text"
                                                                        id="deparments-filter"
                                                                        name="deparments-filter"
                                                                        placeholder="Escribe aquí para buscar"
                                                                        className="w-full border border-solid border-primary p-2 rounded-md"
                                                                        onChange={searchDepartment}
                                                                    />
                                                                </div>

                                                                <div className="flex flex-col overflow-y-auto">
                                                                    {
                                                                        departmentsList.map((departmentItem: any) => (
                                                                            <div
                                                                                key={departmentItem.FldValue}
                                                                                onClick={() => changeDepartment(departmentItem.FldValue, departmentItem.Descr)}
                                                                                className="flex items-center gap-2 px-4 py-2 border-b border-tertiary last:border-b-0 cursor-pointer hover:bg-light"
                                                                            >
                                                                                <p>{departmentItem.Descr}</p>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <p className="font-bold">{clientInformation.departament?.toUpperCase()}</p>
                                                }
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <p className="font-semibold text-medium">MUNICIPIO</p>

                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2 flex flex-col">
                                                        <div
                                                            className={`flex justify-between items-center border pl-2 rounded-md ${selectedMunicipality && selectedMunicipality.hasOwnProperty("municipality") && selectedMunicipality.municipality.length > 0 ? 'border-success' : 'border-danger'}`}
                                                        >
                                                            <p className="py-2">{selectedMunicipality?.municipality?.length > 0 ? selectedMunicipality?.municipality : 'Seleccione un municipio'}</p>

                                                            <div
                                                                onClick={showMunicipalities}
                                                                className={`h-full flex items-center border-l px-4 cursor-pointer ${selectedMunicipality && selectedMunicipality.hasOwnProperty("municipality") && selectedMunicipality.municipality.length > 0 ? 'border-success' : 'border-danger'}`}>
                                                                <IonIcon
                                                                    icon={caretDown}
                                                                    className="text-[20px]"
                                                                />
                                                            </div>

                                                        </div>

                                                        {
                                                            selectedMunicipality?.showOptions &&
                                                            <div className="flex flex-col border-l border-b border-r border-tertiary rounded-b-md max-h-[250px]">
                                                                <div className="p-4 border-b border-tertiary">
                                                                    <input
                                                                        type="text"
                                                                        id="municipalities-filter"
                                                                        name="municipalities-filter"
                                                                        placeholder="Escribe aquí para buscar"
                                                                        className="w-full border border-solid border-primary p-2 rounded-md"
                                                                        onChange={searchMunicipality}
                                                                    />
                                                                </div>

                                                                <div className="flex flex-col overflow-y-auto">
                                                                    {
                                                                        municipalitiesList.map((municipalityItem: any) => (
                                                                            <div
                                                                                key={municipalityItem.FldValue}
                                                                                onClick={() => changeMunicipality(municipalityItem.FldValue, municipalityItem.Descr)}
                                                                                className="flex items-center gap-2 px-4 py-2 border-b border-tertiary last:border-b-0 cursor-pointer hover:bg-light"
                                                                            >
                                                                                <p>{municipalityItem.Descr}</p>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <p className="font-bold">{clientInformation.municipality?.toUpperCase()}</p>
                                                }

                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <p className="font-semibold text-medium">DISTRITO</p>

                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2 flex flex-col">
                                                        <div
                                                            className={`flex justify-between items-center border pl-2 rounded-md ${selectedDistrict && selectedDistrict.hasOwnProperty("district") && selectedDistrict.district.length > 0 ? 'border-success' : 'border-danger'}`}
                                                        >
                                                            <p className="py-2">{selectedDistrict?.district?.length > 0 ? selectedDistrict?.district : 'Seleccione un distrito'}</p>

                                                            <div
                                                                onClick={showDistricts}
                                                                className={`h-full flex items-center border-l px-4 cursor-pointer ${selectedDistrict && selectedDistrict.hasOwnProperty("district") && selectedDistrict.district.length > 0 ? 'border-success' : 'border-danger'}`}>
                                                                <IonIcon
                                                                    icon={caretDown}
                                                                    className="text-[20px]"
                                                                />
                                                            </div>

                                                        </div>

                                                        {
                                                            selectedDistrict?.showOptions &&
                                                            <div className="flex flex-col border-l border-b border-r border-tertiary rounded-b-md max-h-[250px]">
                                                                <div className="p-4 border-b border-tertiary">
                                                                    <input
                                                                        type="text"
                                                                        id="districts-filter"
                                                                        name="districts-filter"
                                                                        placeholder="Escribe aquí para buscar"
                                                                        className="w-full border border-solid border-primary p-2 rounded-md"
                                                                        onChange={searchDistricts}
                                                                    />
                                                                </div>

                                                                <div className="flex flex-col overflow-y-auto">
                                                                    {
                                                                        districtsList.map((districtItem: any) => (
                                                                            <div
                                                                                key={districtItem.FldValue}
                                                                                onClick={() => changeDistrict(districtItem.FldValue, districtItem.Descr)}
                                                                                className="flex items-center gap-2 px-4 py-2 border-b border-tertiary last:border-b-0 cursor-pointer hover:bg-light"
                                                                            >
                                                                                <p>{districtItem.Descr}</p>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <p className="font-bold">{clientInformation.district?.toUpperCase()}</p>
                                                }

                                            </div>
                                        </div>

                                        <div className="w-full grid pb-16 text-mid order-4 gap-6">
                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="email"
                                                            label="CORREO"
                                                            value={email}
                                                            inputMode="text"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={emailValid}
                                                            errorMessage={emailValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">CORREO</p>
                                                        <p className="font-bold">{clientInformation.email}</p>
                                                    </>
                                                }
                                            </div>

                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextArea
                                                            name="contactPerson"
                                                            label="PERSONA DE CONTACTO"
                                                            value={contactPerson}
                                                            inputMode="text"
                                                            changeHandler={onInputChange}
                                                            isFieldValid={contactPersonValid}
                                                            errorMessage={contactPersonValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">PERSONA DE CONTACTO</p>
                                                        <p className="font-bold">{clientInformation.providedBy?.toUpperCase()}</p>
                                                    </>
                                                }
                                            </div>

                                            <div className="flex flex-col">
                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2">
                                                        <TextField
                                                            name="contactPhone"
                                                            label="TELÉFONO DE CONTACTO"
                                                            value={contactPhone}
                                                            inputMode="numeric"
                                                            maxLength={8}
                                                            changeHandler={onInputChange}
                                                            isFieldValid={contactPhoneValid}
                                                            errorMessage={contactPhoneValid}
                                                            required={true}
                                                        />
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <>
                                                        <p className="font-semibold text-medium">TELÉFONO DE CONTACTO</p>
                                                        <p className="font-bold">{clientInformation.phoneNumber}</p>
                                                    </>
                                                }

                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <p className="font-semibold text-medium">TIPO DE EMPRESA</p>

                                                {
                                                    editEnabled &&
                                                    <div className="w-1/2 flex flex-col">
                                                        <div
                                                            className={`flex justify-between items-center border pl-2 rounded-md ${selectedCategory && selectedCategory.hasOwnProperty("nameCategory") && selectedCategory.nameCategory.length > 0 ? 'border-success' : 'border-danger'}`}
                                                        >
                                                            <p className="py-2">{selectedCategory?.nameCategory?.length > 0 ? selectedCategory?.nameCategory : 'Seleccione una categoría'}</p>

                                                            <div
                                                                onClick={showCategories}
                                                                className={`h-full flex items-center border-l px-4 cursor-pointer ${selectedCategory && selectedCategory.hasOwnProperty("nameCategory") && selectedCategory.nameCategory.length > 0 ? 'border-success' : 'border-danger'}`}>
                                                                <IonIcon
                                                                    icon={caretDown}
                                                                    className="text-[20px]"
                                                                />
                                                            </div>

                                                        </div>

                                                        {
                                                            selectedCategory?.showOptions &&
                                                            <div className="flex flex-col border-l border-b border-r border-tertiary rounded-b-md max-h-[250px]">
                                                                <div className="p-4 border-b border-tertiary">
                                                                    <input
                                                                        type="text"
                                                                        id="categories-filter"
                                                                        name="categories-filter"
                                                                        placeholder="Escribe aquí para buscar"
                                                                        className="w-full border border-solid border-primary p-2 rounded-md"
                                                                        onChange={searchCategory}
                                                                    />
                                                                </div>

                                                                <div className="flex flex-col overflow-y-auto">
                                                                    {
                                                                        categoriesList.map((item: any) => (
                                                                            <div
                                                                                key={item.code}
                                                                                onClick={() => changeCategory(item.code, item.name)}
                                                                                className="flex items-center gap-2 px-4 py-2 border-b border-tertiary last:border-b-0 cursor-pointer hover:bg-light"
                                                                            >
                                                                                <p>{item.name}</p>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                }

                                                {
                                                    !editEnabled &&
                                                    <p className="font-bold">{clientInformation.nameCategory?.toUpperCase()}</p>
                                                }

                                            </div>
                                        </div>

                                        <div className="w-4/5 order-5 text-mid flex flex-col gap-4 max-h-[400px] overflow-y-scroll">
                                            <div className="flex items-center gap-2">
                                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M22.9335 12.1048C22.9335 14.0827 22.347 16.0162 21.2481 17.6607C20.1492 19.3053 18.5874 20.5871 16.76 21.344C14.9327 22.1009 12.922 22.2989 10.9821 21.9131C9.04217 21.5272 7.26026 20.5747 5.86168 19.1762C4.46309 17.7776 3.51065 15.9957 3.12478 14.0558C2.73891 12.1159 2.93695 10.1051 3.69386 8.2778C4.45077 6.45046 5.73255 4.88861 7.37711 3.78975C9.02167 2.69088 10.9551 2.10437 12.933 2.10437C14.2463 2.10437 15.5467 2.36304 16.76 2.86561C17.9733 3.36818 19.0758 4.1048 20.0044 5.03343C20.933 5.96205 21.6697 7.06449 22.1722 8.2778C22.6748 9.4911 22.9335 10.7915 22.9335 12.1048ZM19.413 12.1806C19.4124 11.0571 19.1165 9.95357 18.5552 8.98043C17.9938 8.00729 17.1866 7.19874 16.2143 6.63575C15.2421 6.07277 14.1391 5.77511 13.0156 5.77259C11.8922 5.77008 10.7878 6.06279 9.81304 6.62142C9.22138 6.96244 8.68759 7.39519 8.23157 7.90353V7.03574C8.23157 6.90308 8.17887 6.77584 8.08506 6.68204C7.99125 6.58823 7.86402 6.53553 7.73136 6.53553C7.59869 6.53553 7.47146 6.58823 7.37766 6.68204C7.28385 6.77584 7.23115 6.90308 7.23115 7.03574V9.13258C7.23148 9.26503 7.28433 9.39193 7.3781 9.48547C7.47187 9.579 7.59891 9.63153 7.73136 9.63153H9.82567C9.95383 9.62508 10.0746 9.56964 10.163 9.47665C10.2514 9.38367 10.3008 9.26026 10.3008 9.13195C10.3008 9.00364 10.2514 8.88023 10.163 8.78725C10.0746 8.69426 9.95383 8.63882 9.82567 8.63237H8.91999C9.42733 8.04729 10.0546 7.57819 10.7592 7.25693C11.4639 6.93568 12.2294 6.76977 13.0038 6.77048C14.0734 6.77073 15.119 7.08813 16.0083 7.68256C16.8976 8.27698 17.5907 9.12174 17.9999 10.11C18.4091 11.0983 18.5161 12.1857 18.3074 13.2348C18.0986 14.2839 17.5835 15.2476 16.8271 16.0039C16.0708 16.7603 15.1071 17.2754 14.058 17.4842C13.0089 17.6929 11.9215 17.5859 10.9332 17.1767C9.94494 16.7675 9.10018 16.0744 8.50576 15.1851C7.91133 14.2958 7.59392 13.2502 7.59367 12.1806C7.59367 12.0479 7.54097 11.9207 7.44717 11.8269C7.35336 11.7331 7.22613 11.6804 7.09346 11.6804C6.9608 11.6804 6.83357 11.7331 6.73976 11.8269C6.64595 11.9207 6.59325 12.0479 6.59325 12.1806C6.59325 13.0224 6.75907 13.856 7.08123 14.6338C7.40338 15.4115 7.87558 16.1182 8.47085 16.7135C9.06613 17.3088 9.77282 17.781 10.5506 18.1031C11.3283 18.4253 12.1619 18.5911 13.0038 18.5911C13.8456 18.5911 14.6792 18.4253 15.457 18.1031C16.2347 17.781 16.9414 17.3088 17.5367 16.7135C18.132 16.1182 18.6042 15.4115 18.9263 14.6338C19.2485 13.856 19.413 13.0224 19.413 12.1806ZM13.3966 12.0555V8.68037C13.3902 8.55222 13.3347 8.43144 13.2417 8.34302C13.1488 8.2546 13.0254 8.20529 12.897 8.20529C12.7687 8.20529 12.6453 8.2546 12.5523 8.34302C12.4594 8.43144 12.4039 8.55222 12.3975 8.68037V12.269C12.3973 12.3453 12.4146 12.4206 12.4482 12.4891C12.4817 12.5577 12.5304 12.6176 12.5907 12.6644L14.5322 14.6046C14.5785 14.6518 14.6337 14.6894 14.6945 14.7151C14.7554 14.7409 14.8208 14.7543 14.8869 14.7547C14.953 14.755 15.0186 14.7422 15.0797 14.7171C15.1409 14.6919 15.1964 14.6549 15.2432 14.6082C15.2899 14.5614 15.3269 14.5059 15.3521 14.4447C15.3772 14.3836 15.39 14.3181 15.3897 14.2519C15.3893 14.1858 15.3759 14.1204 15.3501 14.0596C15.3244 13.9987 15.2868 13.9435 15.2396 13.8972L13.3966 12.0555Z" fill="#183555" />
                                                </svg>

                                                <p className="font-semibold text-primary">HISTORIAL DE EDICIONES</p>
                                            </div>

                                            {
                                                vatLogs.length > 0 &&
                                                <div className="flex flex-col gap-2">
                                                    <div className="grid grid-cols-12 gap-4">
                                                        <div className="col-span-3">
                                                            <p className="font-semibold text-medium">FECHA</p>
                                                        </div>

                                                        <div className="col-span-9">
                                                            <p className="font-semibold text-medium">EDITADO POR</p>
                                                        </div>
                                                    </div>

                                                    {
                                                        vatLogs.map((item: LogInfo) => (
                                                            <div key={item.createdAt + "_" + item.$key} className="grid grid-cols-12 gap-4">
                                                                <div className="col-span-3">
                                                                    <p className="font-bold">{item.createdAt}</p>
                                                                </div>

                                                                <div className="col-span-9">
                                                                    <p className="font-bold">{item.sellerCode} - {item.sellerName?.toUpperCase()}</p>
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            }
                                        </div>

                                        <div className="w-full order-6 text-mid flex flex-col items-start gap-6">
                                            <div className="grid gap-2">
                                                <p className="font-semibold text-medium">FOTOGRAFÍA FRONTAL DE LA TARJETA</p>

                                                <div className="flex items-center gap-8">
                                                    <div
                                                        onClick={() => clickImageHandler(frontalImageToUpload?.downloadUrl ?? frontalImage)}
                                                        className="w-[256px] min-[425]:w-[300px] max-h-48 bg-light flex justify-center items-center rounded-md iva-card-border-dashed"
                                                    >
                                                        <img
                                                            src={frontalImageToUpload?.downloadUrl ?? frontalImage ?? ''}
                                                            alt="frontal-image"
                                                            className="w-[200px] max-h-48 object-contain cursor-pointer"
                                                        />
                                                    </div>

                                                    {
                                                        editEnabled &&
                                                        <>
                                                            <input
                                                                type='file'
                                                                accept='image/*'
                                                                ref={frontalImageInputRef}
                                                                onChange={(event) => changeIvaCardImage(event, "frontal")}
                                                                style={{ display: 'none' }}
                                                            />

                                                            <button
                                                                onClick={() => frontalImageInputRef.current.click()}
                                                                className="font-bold px-4 py-2 bg-tertiary rounded-md flex items-center gap-2"
                                                            >
                                                                <IonIcon icon={cloudUpload} className="text-[20px]" />
                                                                Cambiar fotografía
                                                            </button>
                                                        </>
                                                    }
                                                </div>
                                            </div>

                                            <div className="grid gap-2">
                                                <p className="font-semibold text-medium">FOTOGRAFÍA POSTERIOR DE LA TARJETA</p>

                                                <div className="flex items-center gap-8">
                                                    <div
                                                        onClick={() => clickImageHandler(backImageToUpload?.downloadUrl ?? backImage)}
                                                        className="w-[256px] min-[425]:w-[300px] max-h-48 bg-light flex justify-center items-center rounded-md iva-card-border-dashed"
                                                    >
                                                        <img
                                                            src={backImageToUpload?.downloadUrl ?? backImage ?? ''}
                                                            alt="rear-image"
                                                            className="w-[200px] max-h-48 object-contain cursor-pointer"
                                                        />
                                                    </div>

                                                    {
                                                        editEnabled &&
                                                        <>
                                                            <input
                                                                type='file'
                                                                accept='image/*'
                                                                ref={backImageInputRef}
                                                                onChange={(event) => changeIvaCardImage(event, "back")}
                                                                style={{ display: 'none' }}
                                                            />

                                                            <button
                                                                onClick={() => backImageInputRef.current.click()}
                                                                className="font-bold px-4 py-2 bg-tertiary rounded-md flex items-center gap-2"
                                                            >
                                                                <IonIcon icon={cloudUpload} className="text-[20px]" />
                                                                Cambiar fotografía
                                                            </button>
                                                        </>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </IonContent>

            {
                (showImageModal || savingInformation || showCancellationConfirmationModal || showEditConfirmationModal || errorSavingInformation || informationSavedSuccessfully || changingCardStatus || gettingLists) &&
                <ModalBackground />
            }

            {
                showImageModal &&
                <ImageVisualizer imageArray={imageToShow} imageAlt="document" clickOutsideHandler={() => setShowImageModal(false)} />
            }

            {
                savingInformation &&
                <LoadingModal whiteBackground={true} text="Guardando información..." />
            }

            {
                gettingLists &&
                <LoadingModal whiteBackground={false} />
            }

            {
                showEditConfirmationModal &&
                <QuestionModal
                    question={`<p><strong>¿Está seguro que desea guardar la información actual?</strong> Tenga en cuenta que toda modificación realizada a la información quedará registrada</p>`}
                    cancelClickHandler={() => setShowEditConfirmationModal(false)}
                    acceptClickHandler={saveNewInfo}
                />
            }

            {
                showCancellationConfirmationModal &&
                <QuestionModal
                    question={`<p><strong>¿Está seguro que desea salir de la pantalla actual?</strong> Se perderán todas las modificaciones hechas a la información que no hayan sido guardadas</p>`}
                    cancelClickHandler={() => setShowCancellationConfirmationModal(false)}
                    acceptClickHandler={() => { setShowCancellationConfirmationModal(false); ionRouter.push("/ivaCards", "back", "push") }}
                />
            }

            {
                errorSavingInformation &&
                <InformativeModal
                    type="warning"
                    title="Advertencia"
                    text="Ha ocurrido un error al guardar la información. Intenta realizar nuevamente el proceso y si el error persiste comúnicate con el equipo de soporte técnico"
                    acceptClickHandler={() => setErrorSavingInformation(false)}
                />
            }

            {
                informationSavedSuccessfully &&
                <InformativeModal
                    type="success"
                    title="La información ha sido actualizada éxitosamente"
                    acceptClickHandler={acceptSuccessfulMessageHandler}
                />
            }

            {
                changingCardStatus &&
                <LoadingModal whiteBackground={true} text="Actualizando estado..." />
            }
        </IonPage>
    )
}